import { useMemo } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { RoadDetailInfo, useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { APP_CAMPAIGN, MODULE_CAMPAIGNS, STATUS_DELIVERED } from "@l2r-front/l2r-shared-campaign"
import { IconButton, Typography, Tooltip, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"


import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_HORIZONTAL_SIGNING } from "../../../../common/constants/modules"
import { useHorizontalSign } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"

import { HorizontalSignDetails } from "../HorizontalSignDetails"
import { HorizontalSignDocumentList } from "../HorizontalSignDocumentList"

import * as Styled from "./HorizontalSignDetailsSidebarContent.styled"

export function HorizontalSignDetailsSidebarContent(props) {
    const {
        onDetachCampaign,
    } = props

    const { signId, road } = useParams()
    const { data, isError } = useHorizontalSign(signId)
    const isMobile = useIsMobileDevice()
    const navigate = useNavigateWithSearchParams()
    const { setAlert } = useAlertsDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const latLong = data?.geometry.coordinates
    const { selectedNetwork } = useNetworksStateContext()
    const readOnly = useRoleIsReadOnly(data?.sub_networks, API_MODULE_HORIZONTAL_SIGNING)

    const handleCampaignUnlink = (campaign) => {
        if (onDetachCampaign) {
            setAlert({
                alertTitle: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.detachCampain.confirmation", { campaignName: campaign.name }),
                cancelText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.detachCampain.cancel"),
                confirmText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.detachCampain.confirm"),
                callback: () => onDetachCampaign(campaign.uuid),
                severity: "warning",
            })
        }
    }

    const sortedCampaigns = useMemo(() => {
        return [...(data?.campaigns ?? [])].sort((campaignA, campaignB) => {
            if (campaignA.status === STATUS_DELIVERED && campaignB.status !== STATUS_DELIVERED) {
                return 1
            }
            if (campaignA.status !== STATUS_DELIVERED && campaignB.status === STATUS_DELIVERED) {
                return -1
            }
            if (campaignA.status === STATUS_DELIVERED && campaignB.status === STATUS_DELIVERED) {
                return new Date(campaignA.updated_at) - new Date(campaignB.updated_at)
            }
            return new Date(campaignB.created_at) - new Date(campaignA.created_at)
        })
    }, [data?.campaigns])

    const LatLongComponent = useMemo(() => {
        if (latLong) {
            return <Typography id="road-detail-latlong" variant="Desktop & Mobile/Body/Regular">
                {latLong[1].toFixed(5)}, {latLong[0].toFixed(5)}
            </Typography>
        }
        return <Styled.Skeleton />
    }, [latLong])

    if (!data) {
        return null
    }

    return <Styled.Container isMobile={isMobile}>
        <Styled.RoadDetailWrapper>
            <RoadDetailInfo
                LengthComponent={LatLongComponent}
                lengthLabel={`${t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.location.latlong")} `}
                road={road}
            />
        </Styled.RoadDetailWrapper >
        <Styled.Wrapper isMobile={isMobile}>
            {
                isError ? <Styled.ErrorText>
                    {t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.content.error")}
                </Styled.ErrorText>
                    : <HorizontalSignDetails horizontalSign={data} />
            }
            {sortedCampaigns.length ? <Styled.CampaignsWrapper>
                <Typography variant="Desktop & Mobile/Heading/H3">
                    {t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.content.campaigns")}
                </Typography>
                {sortedCampaigns.map(campaign => (
                    <Styled.CampaignItem key={campaign.uuid}>
                        <Styled.CampaignButton key={campaign.uuid} onClick={() => {
                            navigate(`/${selectedNetwork.slug}/${APP_CAMPAIGN}/${MODULE_CAMPAIGNS}/${campaign.uuid}`)
                        }}>
                            <Styled.ColorContainer>
                                <Styled.Color color={campaign.color} disabled={campaign.status === STATUS_DELIVERED} />
                            </Styled.ColorContainer>
                            <Styled.CampaignName variant="Desktop & Mobile/Body/Label & Helper text" disabled={campaign.status === STATUS_DELIVERED}>
                                {campaign.name}
                            </Styled.CampaignName>
                        </Styled.CampaignButton>
                        {(!readOnly && campaign.status !== STATUS_DELIVERED) && <Tooltip disableFocusListener title={t(I18N_NAMESPACE, "containers.horizontalSignCampaignsList.tooltip")}>
                            <IconButton onClick={() => handleCampaignUnlink(campaign)}>
                                <Styled.LinkOffIcon />
                            </IconButton>
                        </Tooltip>}

                    </Styled.CampaignItem >
                ))
                }
            </Styled.CampaignsWrapper > : null}
        </Styled.Wrapper>
        <HorizontalSignDocumentList signId={signId} />
    </Styled.Container >
}